import { unwrap } from "./utils/null";

export interface Config {
  version: string | null;
  skygear: SkygearConfig;
  feature: {
    stripePayment: boolean;
    stripeTokenizeCard: boolean;
    customerPortalLocale: boolean;
  };
}

export interface SkygearConfig {
  apiKey: string;
  endpoint: string;
}

type RawConfigName =
  | "APP_VERSION"
  | "APP_SKYGEAR_APIKEY"
  | "APP_SKYGEAR_ENDPOINT"
  | "APP_FEATURE_STRIPE_PAYMENT"
  | "APP_FEATURE_STRIPE_TOKENIZE_CARD"
  | "APP_FEATURE_CUSTOMER_PORTAL_LOCALE";

// 1. Undefined config is not allowed.
// 2. Optional config is undefined when not provided.
// 3. Optional config should have default.
// 4. Default value should be string or null.
function loadConfig(name: RawConfigName, defaultValue: string): string;
function loadConfig(name: RawConfigName, defaultValue?: null): string | null;
function loadConfig(
  name: RawConfigName,
  defaultValue?: string | null
): string | null {
  const rawConfig = window.rawConfig;

  // NOTE: when run in test, we don't have a config!
  if (rawConfig === undefined) return defaultValue || "";

  const rawValue = rawConfig[name];
  const value: string | null | undefined =
    rawValue !== `$[${name}]` ? rawValue : defaultValue;
  if (value === undefined) {
    throw new TypeError(`Config ${name} is not set.`);
  }
  return value;
}

function getConfig(): Config {
  const version = loadConfig("APP_VERSION", null);
  const skygearApiKey = unwrap(loadConfig("APP_SKYGEAR_APIKEY"));
  const skygearEndpoint = unwrap(loadConfig("APP_SKYGEAR_ENDPOINT"));
  const enableStripePayment =
    unwrap(loadConfig("APP_FEATURE_STRIPE_PAYMENT", "false")) === "true";
  const enableStripeTokenizeCard =
    unwrap(loadConfig("APP_FEATURE_STRIPE_TOKENIZE_CARD", "false")) === "true";
  const enableCustomerPortalLocale =
    unwrap(loadConfig("APP_FEATURE_CUSTOMER_PORTAL_LOCALE", "false")) ===
    "true";

  const skygearConfig: SkygearConfig = {
    apiKey: skygearApiKey,
    endpoint: skygearEndpoint,
  };
  return {
    version,
    skygear: skygearConfig,
    feature: {
      stripePayment: enableStripePayment,
      stripeTokenizeCard: enableStripeTokenizeCard,
      customerPortalLocale: enableCustomerPortalLocale,
    },
  };
}

const config = getConfig();
export default config;
